import type { CoopClue, CoopCrosswordBoard } from '../../core/types/payloads/coop.types';
import { buildCrosswordGrid } from './crossword.engine';

export interface CrosswordWordEntry {
  word: string;
  clue: string;
}

const PAD = 1;

function normalizeWord(raw: string): string {
  return raw
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-ZÑ]/g, '');
}

function cellKey(r: number, c: number): string {
  return `${r}:${c}`;
}

type Dir = 'across' | 'down';

interface Placement {
  word: string;
  clue: string;
  row: number;
  col: number;
  direction: Dir;
}

function lettersFor(p: Placement): Array<{ r: number; c: number; ch: string }> {
  const out: Array<{ r: number; c: number; ch: string }> = [];
  for (let i = 0; i < p.word.length; i++) {
    const r = p.direction === 'down' ? p.row + i : p.row;
    const c = p.direction === 'across' ? p.col + i : p.col;
    out.push({ r, c, ch: p.word[i]! });
  }
  return out;
}

function canPlace(
  grid: Map<string, string>,
  word: string,
  row: number,
  col: number,
  direction: Dir,
): boolean {
  const len = word.length;
  if (len < 2) return false;

  const dr = direction === 'down' ? 1 : 0;
  const dc = direction === 'across' ? 1 : 0;

  const beforeR = row - dr;
  const beforeC = col - dc;
  if (grid.has(cellKey(beforeR, beforeC))) return false;

  const afterR = row + dr * len;
  const afterC = col + dc * len;
  if (grid.has(cellKey(afterR, afterC))) return false;

  let intersects = false;

  for (let i = 0; i < len; i++) {
    const r = row + dr * i;
    const c = col + dc * i;
    const key = cellKey(r, c);
    const ch = word[i]!;
    const existing = grid.get(key);

    if (existing !== undefined) {
      if (existing !== ch) return false;
      intersects = true;
    } else {
      const perpKeys =
        direction === 'across'
          ? [cellKey(r - 1, c), cellKey(r + 1, c)]
          : [cellKey(r, c - 1), cellKey(r, c + 1)];

      for (const pk of perpKeys) {
        if (grid.has(pk)) intersects = true;
      }
    }
  }

  return grid.size === 0 || intersects;
}

function applyPlacement(grid: Map<string, string>, p: Placement): void {
  for (const { r, c, ch } of lettersFor(p)) {
    grid.set(cellKey(r, c), ch);
  }
}

function removePlacement(grid: Map<string, string>, p: Placement): void {
  for (const { r, c } of lettersFor(p)) {
    const key = cellKey(r, c);
    let shared = false;
    for (const other of grid.entries()) {
      if (other[0] === key) continue;
    }
    void shared;
    grid.delete(key);
  }
  // Rebuild would be safer — use snapshot approach in backtrack instead
}

function cloneGrid(grid: Map<string, string>): Map<string, string> {
  return new Map(grid);
}

function boundsFromGrid(grid: Map<string, string>): { minR: number; minC: number; maxR: number; maxC: number } {
  let minR = 0;
  let minC = 0;
  let maxR = 0;
  let maxC = 0;
  let first = true;
  for (const key of grid.keys()) {
    const [r, c] = key.split(':').map(Number);
    if (first) {
      minR = maxR = r;
      minC = maxC = c;
      first = false;
    } else {
      minR = Math.min(minR, r);
      minC = Math.min(minC, c);
      maxR = Math.max(maxR, r);
      maxC = Math.max(maxC, c);
    }
  }
  return { minR, minC, maxR, maxC };
}

function placementsToBoard(placements: Placement[]): CoopCrosswordBoard {
  const grid = new Map<string, string>();
  for (const p of placements) {
    applyPlacement(grid, p);
  }

  const { minR, minC, maxR, maxC } = boundsFromGrid(grid);
  const rowOff = PAD - minR;
  const colOff = PAD - minC;
  const rows = maxR - minR + 1 + PAD * 2;
  const cols = maxC - minC + 1 + PAD * 2;

  const clues: CoopClue[] = placements.map((p, i) => ({
    id: `clue_${i + 1}`,
    direction: p.direction,
    row: p.row + rowOff,
    col: p.col + colOff,
    length: p.word.length,
    clue: p.clue.trim() || p.word,
    answer: p.word,
  }));

  return { kind: 'crossword', rows, cols, clues };
}

function findCandidates(
  grid: Map<string, string>,
  word: string,
): Array<{ row: number; col: number; direction: Dir }> {
  const out: Array<{ row: number; col: number; direction: Dir }> = [];

  if (grid.size === 0) {
    out.push({ row: 0, col: 0, direction: 'across' });
    return out;
  }

  for (const [key, letter] of grid.entries()) {
    const [r, c] = key.split(':').map(Number);
    for (let i = 0; i < word.length; i++) {
      if (word[i] !== letter) continue;
      for (const direction of ['across', 'down'] as Dir[]) {
        const row = direction === 'down' ? r - i : r;
        const col = direction === 'across' ? c - i : c;
        if (canPlace(grid, word, row, col, direction)) {
          out.push({ row, col, direction });
        }
      }
    }
  }

  return out;
}

function backtrack(
  entries: CrosswordWordEntry[],
  index: number,
  grid: Map<string, string>,
  placements: Placement[],
): Placement[] | null {
  if (index >= entries.length) return placements;

  const { word, clue } = entries[index]!;
  const candidates = findCandidates(grid, word);

  for (const cand of candidates) {
    const placement: Placement = { word, clue, ...cand };
    const nextGrid = cloneGrid(grid);
    applyPlacement(nextGrid, placement);
    const result = backtrack(entries, index + 1, nextGrid, [...placements, placement]);
    if (result) return result;
  }

  return null;
}

function shuffleWithSeed<T>(arr: T[], seed: number): T[] {
  const out = [...arr];
  let s = seed;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const j = s % (i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export interface LayoutCrosswordResult {
  board: CoopCrosswordBoard;
  placed: number;
  total: number;
}

export function layoutCrosswordFromWords(
  rawEntries: CrosswordWordEntry[],
  attempt = 0,
): LayoutCrosswordResult | null {
  const entries = rawEntries
    .map((e) => ({ word: normalizeWord(e.word), clue: e.clue.trim() }))
    .filter((e) => e.word.length >= 2);

  if (entries.length === 0) return null;

  const sorted = [...entries].sort((a, b) => b.word.length - a.word.length);
  const ordered = shuffleWithSeed(sorted, attempt + 1);

  for (let tryFlip = 0; tryFlip < 2; tryFlip++) {
    const tryOrder = tryFlip === 0 ? ordered : [...ordered].reverse();
    const grid = new Map<string, string>();
    const result = backtrack(tryOrder, 0, grid, []);
    if (!result) continue;

    const board = placementsToBoard(result);
    try {
      buildCrosswordGrid(board);
      return { board, placed: result.length, total: entries.length };
    } catch {
      continue;
    }
  }

  return null;
}

export function parseWordDump(text: string): CrosswordWordEntry[] {
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const out: CrosswordWordEntry[] = [];

  for (const line of lines) {
    const arrow = line.split(/\s*(?:→|->|:|\|)\s*/);
    if (arrow.length >= 2) {
      out.push({ word: arrow[0]!.trim(), clue: arrow.slice(1).join(' ').trim() });
      continue;
    }
    const tab = line.split(/\t+/);
    if (tab.length >= 2) {
      out.push({ word: tab[0]!.trim(), clue: tab[1]!.trim() });
    }
  }

  return out;
}
